import { Link } from "react-router-dom";

import { formatDate } from "../lib/format.js";
import type { ReviewSummary } from "../lib/types.js";
import { Rating } from "./Rating.js";

/**
 * The film line that sits under a review's headline.
 *
 * A review usually has a headline of its own — "The Ache of the Road Not
 * Taken" over *Past Lives* — and then the film is worth naming underneath. But
 * plenty of Davis's pieces are headlined with the film itself, and printing
 * "Superman" directly above "Superman (2025)" reads as a bug. When the two
 * match, only the year is worth saying.
 */
function filmLine(review: ReviewSummary): string | null {
  const sameAsTitle =
    review.title.trim().toLowerCase() === review.filmTitle.trim().toLowerCase();

  if (sameAsTitle) return review.filmYear ? String(review.filmYear) : null;

  return review.filmYear
    ? `${review.filmTitle} (${review.filmYear})`
    : review.filmTitle;
}

interface ReviewCardProps {
  review: ReviewSummary;
  /** `feature` is the larger treatment used for the newest review on the home page. */
  variant?: "default" | "feature" | "compact";
}

export function ReviewCard({ review, variant = "default" }: ReviewCardProps) {
  if (variant === "compact") return <CompactCard review={review} />;
  if (variant === "feature") return <FeatureCard review={review} />;

  return (
    <article className="group relative flex flex-col">
      <Poster review={review} className="aspect-[3/2]" />

      <div className="mt-4 flex flex-1 flex-col">
        <TagRow tags={review.tags} />

        <h3 className="mt-2 font-display text-xl leading-snug text-paper-50">
          <Link to={`/reviews/${review.slug}`} className="after:absolute after:inset-0">
            {review.title}
          </Link>
        </h3>

        {filmLine(review) && (
          <p className="mt-1 text-sm text-paper-400">{filmLine(review)}</p>
        )}

        {review.excerpt && (
          <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-paper-400">
            {review.excerpt}
          </p>
        )}

        <div className="mt-4 flex items-center justify-between border-t border-ink-700 pt-3">
          <Rating value={review.rating} size="sm" />
          <Meta review={review} />
        </div>
      </div>
    </article>
  );
}

function FeatureCard({ review }: { review: ReviewSummary }) {
  return (
    <article className="group relative grid gap-8 lg:grid-cols-2 lg:items-center">
      <Poster review={review} className="aspect-[16/10]" priority />

      <div>
        <TagRow tags={review.tags} />

        <h2 className="mt-3 font-display text-4xl leading-[1.05] text-paper-50 sm:text-5xl">
          <Link to={`/reviews/${review.slug}`} className="after:absolute after:inset-0">
            {review.title}
          </Link>
        </h2>

        {filmLine(review) && (
          <p className="mt-3 text-base text-paper-400">{filmLine(review)}</p>
        )}

        {review.excerpt && (
          <p className="mt-5 max-w-prose text-base leading-relaxed text-paper-200">
            {review.excerpt}
          </p>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2">
          <Rating value={review.rating} size="lg" />
          <Meta review={review} />
        </div>
      </div>
    </article>
  );
}

function CompactCard({ review }: { review: ReviewSummary }) {
  return (
    <article className="group relative flex items-center gap-4 border-b border-ink-700 py-4">
      <div className="min-w-0 flex-1">
        <h3 className="truncate font-display text-lg text-paper-50">
          <Link to={`/reviews/${review.slug}`} className="after:absolute after:inset-0">
            {review.title}
          </Link>
        </h3>
        {filmLine(review) && (
          <p className="mt-0.5 truncate text-sm text-paper-400">{filmLine(review)}</p>
        )}
      </div>

      <Rating value={review.rating} size="sm" />
    </article>
  );
}

function Poster({
  review,
  className,
  priority = false,
}: {
  review: ReviewSummary;
  className: string;
  priority?: boolean;
}) {
  return (
    <div
      // Lifts and warms its border on card hover. The inner image already
      // scales; moving the frame as well is what makes the card feel like one
      // object responding rather than a picture zooming inside a static box.
      className={`relative overflow-hidden rounded-sm border border-ink-700 bg-ink-800 transition-[transform,border-color,box-shadow] duration-300 group-hover:-translate-y-1 group-hover:border-lamp-400/60 group-hover:shadow-[0_18px_36px_-16px_rgba(0,0,0,0.9)] ${className}`}
    >
      {review.coverImageUrl ? (
        <img
          src={review.coverImageUrl}
          // The review title already describes the image's subject; a still
          // from the film is decorative next to it, so the alt text names the
          // film rather than repeating the headline.
          alt={`Still from ${review.filmTitle}`}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
        />
      ) : (
        <div
          aria-hidden="true"
          className="flex h-full w-full items-center justify-center bg-gradient-to-br from-ink-800 to-ink-700"
        >
          <span className="font-display text-5xl text-ink-500">
            {review.filmTitle.slice(0, 1).toUpperCase()}
          </span>
        </div>
      )}

      {!review.published && (
        <span className="absolute left-3 top-3 rounded-sm bg-reel-400 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider text-ink-950">
          Draft
        </span>
      )}
    </div>
  );
}

function TagRow({ tags }: { tags: string[] }) {
  if (tags.length === 0) return null;

  return (
    <ul className="flex flex-wrap gap-2">
      {tags.slice(0, 3).map((tag) => (
        <li
          key={tag}
          className="text-[0.65rem] uppercase tracking-[0.18em] text-lamp-400"
        >
          {tag}
        </li>
      ))}
    </ul>
  );
}

function Meta({ review }: { review: ReviewSummary }) {
  const date = review.publishedAt ?? review.createdAt;

  return (
    <p className="text-xs text-paper-500">
      <time dateTime={date}>{formatDate(date)}</time>
      {/* No count while discussion is off — it would advertise a thread the
          card links to and the review page does not show. */}
      {review.commentsEnabled && review.commentCount > 0 && (
        <>
          {" · "}
          {review.commentCount} comment{review.commentCount === 1 ? "" : "s"}
        </>
      )}
    </p>
  );
}
